
import { useEffect, useMemo, useRef, useState } from 'react';
import { ExpressCheckoutElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { isEmpty } from 'lodash';
import { IAccountPreference, ISessionKey } from '@share/common-types';
import {
  computeIsStripePaymentMethodsEnabled,
  FunctionalRootState,
  getSessionObj,
  hasStripeExpressWalletMethods,
  LoginType,
  UrlUtils,
} from '@share/utils';
import { CONDO_SESSION_KEY, SESSION_KEY_LABEL, SESSION_KEY_LABEL_ROOMS } from '@constants';
import { useDispatch, useSelector } from 'react-redux';
import { getBookPackageWithCard, getCarsBookPackageWithCard, getCondoBookPackageWithCard, getWeeksBookPackageWithCard, ICarsDetailsState, ICarsReviewBookState, ICondoReviewBookState, ICouponState, IReviewBookState, IWeeksReviewBookState } from '@store/slices';
import { IHotelsState, ILoginState, IMarginatorState } from '@share/store/slices';
import { IFormErrors, ScrollBehaviorEnum } from '@common-types';
import moment from 'moment';
import { CONDO_UNITS_SESSION_KEY_LABEL, DATE_FORMAT_FILTER, LIFE_STYLE_NAME_PARAM, QUOTE_LABEL, SESSION_KEY_TOKEN_NAME_PARAM, WEEKS_UNITS_SESSION_KEY_LABEL } from '@share/constants';
import { StripeBtnTypes } from '../../utils/stripe';
import { getAvailabilityId, getCondoId, getWeeksAvailabilityId, removingDashFromString } from '@utils';

interface IProps {
  totalPrice: number;
  formsRef: any;
  isDisabled?: boolean;
  type: string;
  scrollToError?: () => void;
  setMultiplePayments?: (payment: any) => void;
  paymentSelected: string;
  onExpressWalletAvailabilityResolved?: (hasWallets: boolean) => void;
}

// Tipo de las opciones de pago
type ExpressCheckoutPaymentMethodsOption = {
  amazonPay: 'auto' | 'never'; // 'auto' shows Amazon Pay if Stripe deems it optimal for conversion
  applePay: 'always' | 'never'; // 'always' shows Apple Pay on supported platforms
  googlePay: 'always' | 'never';  // 'always' shows Google Pay on supported platforms
  link: 'auto' | 'never'; // Show Link if Stripe's auto rules deem it optimal
  paypal: 'auto' | 'never'; // Show PayPal if  available and Stripe allows
};

export default function ExpressCheckoutButtons(props: IProps) {
  const {
    totalPrice,
    formsRef,
    isDisabled,
    type,
    scrollToError,
    setMultiplePayments,
    paymentSelected,
    onExpressWalletAvailabilityResolved,
  } = props;

  const [visibility, setVisibility] = useState('hidden');
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();
  
  const reviewBookStore: IReviewBookState = useSelector(
    (state: FunctionalRootState) => state.reviewBookStore,
  );
  const marginatorStore: IMarginatorState = useSelector(
    (state: FunctionalRootState) => state.marginatorStore,
  );
  const carsReviewBookStore: ICarsReviewBookState = useSelector(
    (state: FunctionalRootState) => state.carsReviewBookStore,
  );
  const carsDetailsStore: ICarsDetailsState = useSelector(
    (state: FunctionalRootState) => state.carsDetailsStore,
  );
  const weeksReviewBookStore: IWeeksReviewBookState = useSelector(
    (state: FunctionalRootState) => state.weeksReviewBookStore,
  );

  const { account }: ILoginState = useSelector((state: FunctionalRootState) => state.loginStore);

  const navigationMenuStore = useSelector((state: FunctionalRootState) => state.navigationMenuStore);

  const { condoBookingSummary, guest: condoGuest }: ICondoReviewBookState = useSelector(
    (state: FunctionalRootState) => state.condoReviewBookStore,
  );

  const { coupon }: ICouponState = useSelector(
    (state: FunctionalRootState) => state.redeemCodeStore,
  );

  const { agencyNumber, referenceNumber }: IHotelsState = useSelector((state: FunctionalRootState) => state.hotelsStore);

  const { guests, bookingSummary } = reviewBookStore;
  const { stripe: stripeInfo, driver } = carsReviewBookStore;
  const { car } = carsDetailsStore;

  const { weeksBookingSummary } = weeksReviewBookStore;
  const { guest: weeksGuest } = weeksReviewBookStore;


  const buttonsPreferences = account?.preferences.filter(
    (x: IAccountPreference) => x.key === 'payment_buttons',
  );

  const ZERO = 0;

  const paymentMethod = useMemo(() => {
    return paymentSelected === 'amazon_pay' ? 'Amazon pay' : 
    paymentSelected === 'apple_pay' ? 'Apple pay' : 
    paymentSelected === 'google_pay' ? 'Google pay' : 
    paymentSelected === 'link' ? 'Link' : paymentSelected === 'paypal' ? 'Paypal' : 
    paymentSelected === 'card' ? 'Card' : 'None';
  }, [buttonsPreferences, paymentSelected]);

  const isStripeExpressEnabled = computeIsStripePaymentMethodsEnabled(
    account,
    navigationMenuStore?.items?.siteId,
    navigationMenuStore?.items?.organizationId,
  );

  const onExpressResolvedRef = useRef(onExpressWalletAvailabilityResolved);
  onExpressResolvedRef.current = onExpressWalletAvailabilityResolved;

  /** Last boolean we told the parent; null = no definitive onReady yet. */
  const lastAvailabilityReportRef = useRef<boolean | null>(null);
  const walletProbeFallbackTimerRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    lastAvailabilityReportRef.current = null;
    if (walletProbeFallbackTimerRef.current != null) {
      window.clearTimeout(walletProbeFallbackTimerRef.current);
      walletProbeFallbackTimerRef.current = undefined;
    }
    if (!isStripeExpressEnabled || !onExpressWalletAvailabilityResolved) {
      return;
    }
    walletProbeFallbackTimerRef.current = window.setTimeout(() => {
      if (lastAvailabilityReportRef.current !== true) {
        lastAvailabilityReportRef.current = false;
        onExpressResolvedRef.current?.(false);
      }
    }, 8000);
    return () => {
      if (walletProbeFallbackTimerRef.current != null) {
        window.clearTimeout(walletProbeFallbackTimerRef.current);
      }
    };
  }, [isStripeExpressEnabled, onExpressWalletAvailabilityResolved]);

  if (!isStripeExpressEnabled) {
    return null;
  }

  const onConfirm = async (_event: any) => {

    if (!stripe || !elements) {
      // Stripe.js hasn't loaded yet.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    await elements?.submit();

    const values = UrlUtils.getValues();

    let quote;
    if (values[QUOTE_LABEL]) {
      quote = values[QUOTE_LABEL] as string;
    }

    if (type === StripeBtnTypes.Cars) {
      const { driverForm, emailForm, billingForm } = formsRef;
      const formDriverRefValidate = driverForm.current.validateFields();
      const formEmailRefValidate = emailForm.current.validateFields();
      const formBillingRefValidate = billingForm.current.validateFields();
      const carsData: any = {
        id: car?.id,
        customer: {
          primary: {
            personName: {
              givenName: driver?.givenName,
              surname: driver?.surname,
            },
            telephone: {
              phoneTechType: '1',
              phoneNumber: driver?.phone,
            },
            email: driver?.email,
            address: {
              streetNmbr: stripeInfo?.addressLine,
              cityName: stripeInfo?.city,
              postalCode: stripeInfo?.zipCode,
              stateCode: stripeInfo?.state ? stripeInfo?.state : 'AK',
              stateProv: stripeInfo?.state ? stripeInfo?.state : 'AK',
              countryCode: driver?.country,
            },
            countryCode: stripeInfo?.country,
            birthDate: `${moment(driver?.birthDate, DATE_FORMAT_FILTER).format('yyyy-MM-DD')}`,
            language: 'EN',
          },
        },
        currency: account?.currency,
        marginator: {
          percentage: marginatorStore.percentage,
          commission: marginatorStore.commission,
          impersonator: {
            memberId: marginatorStore.impersonator.memberId,
          },
        },
        isCheckoutExpress: true,
      };

      Promise.all([formDriverRefValidate, formBillingRefValidate, formEmailRefValidate])
        .then(() => {
          dispatch(getCarsBookPackageWithCard(carsData, stripe, elements, paymentMethod));
        })
        .catch((error: IFormErrors) => {
          const { errorFields } = error;
          const { name } = errorFields[ZERO];

          const { scrollToField: scrollToDriverField } = driverForm.current;
          const { scrollToField: scrollToBillingField } = billingForm.current;
          const { scrollToField: scrollToEmailField } = emailForm.current;

          (() => {
            scrollToDriverField(name[ZERO], { behavior: ScrollBehaviorEnum.Smooth });
            scrollToBillingField(name[ZERO], { behavior: ScrollBehaviorEnum.Smooth });
            scrollToEmailField(name[ZERO], { behavior: ScrollBehaviorEnum.Smooth });
          })();
        });
    } else if (type === StripeBtnTypes.Hotel) {
      const sessionKey = getSessionObj(
        values[SESSION_KEY_LABEL_ROOMS] as ISessionKey,
        values[SESSION_KEY_LABEL] as ISessionKey,
      );
      const { formGuest, formEmail } = formsRef;
      const formGuestRefValidate = formGuest.current.validateFields();
      const formEmailRefValidate = formEmail.current.validateFields();
      const getLeadPax = guests.find((guest: any) => guest?.isMainGuest);

      const data: any = {
        sessionKey,
        bookingPrice: totalPrice,
        hotelId: bookingSummary?.bookingCard.package.hotelId,
        leadPaxId: getLeadPax?.id,
        leadPaxRoomId: getLeadPax?.allocation,
        packageId: bookingSummary?.bookingCard.package.packageId,
        marginator: {
          percentage: marginatorStore.percentage,
          commission: marginatorStore.commission,
          impersonator: {
            memberId: marginatorStore.impersonator.memberId,
          },
        },
        isCheckoutExpress: true,
        guests: guests.map((guest: any) => {
          const type: string = guest?.type;

          return {
            ...guest,
            namePrefix: !isEmpty(guest?.namePrefix) ? guest?.namePrefix : 'Mr',
            givenName: !isEmpty(guest?.givenName) ? guest?.givenName : 'TBV',
            surname: !isEmpty(guest?.surname) ? guest?.surname : 'TBV',
            type,
          };
        }),
        currency: account?.currency,
        referenceNumber,
        agencyNumber,
        couponCode: coupon,
        quote,
      };

      Promise.all([formGuestRefValidate, formEmailRefValidate])
        .then(() => {
          dispatch(getBookPackageWithCard(data, stripe as any, elements as any, paymentMethod));
        })
        .catch((error: IFormErrors) => {
          const { errorFields } = error;
          const { name } = errorFields[ZERO];

          const { scrollToField: scrollToGuestField } = formGuest.current;
          const { scrollToField: scrollToEmailField } = formEmail.current;

          (() => {
            scrollToEmailField(name[ZERO], { behavior: ScrollBehaviorEnum.Smooth });
            scrollToGuestField(name[ZERO], { behavior: ScrollBehaviorEnum.Smooth });
          })();
        });
    } else if (type === StripeBtnTypes.Condo) {
      const sessionKey = getSessionObj(
        values[CONDO_UNITS_SESSION_KEY_LABEL] as ISessionKey,
        values[CONDO_SESSION_KEY] as ISessionKey,
      );

      const data: any = {
        sessionKey,
        bookingPrice: totalPrice,
        condoId: getCondoId(),
        availabilityId: getAvailabilityId(),
        couponCode: coupon,
        guests: [{ ...condoGuest, phone: removingDashFromString(condoGuest.phone) }],
        supplierType: condoBookingSummary?.supplierType ?? '',
        quote,
        isCheckoutExpress: true,
      };
      dispatch(
        getCondoBookPackageWithCard(
          data,
          () => {
            scrollToError?.();
          },
          stripe,
          elements as any,
          paymentMethod
        ),
      );
    } else if (type === StripeBtnTypes.Weeks) {
      const sessionKey = getSessionObj(
        values[WEEKS_UNITS_SESSION_KEY_LABEL] as ISessionKey,
        values[CONDO_SESSION_KEY] as ISessionKey,
      );

      const data: any = {
        sessionKey,
        bookingPrice: totalPrice,
        condoId: getCondoId() ? getCondoId() : null,
        availabilityId: getWeeksAvailabilityId() ? getWeeksAvailabilityId() : null,
        couponCode: coupon,
        guests: [{ ...weeksGuest, phone: removingDashFromString(weeksGuest.phone) }],
        supplierType: weeksBookingSummary.supplierType,
        quote,
        isCheckoutExpress: true,
      };

      const isPrivateWithToken = account?.type === LoginType.PrivateWithToken;
      const privateTokenFromUrl = account?.privateTokenFromUrl;
      if (isPrivateWithToken && privateTokenFromUrl) {
        data.ssoSessionKey = UrlUtils.getSearchParam(SESSION_KEY_TOKEN_NAME_PARAM);
        data.lifeType = UrlUtils.getSearchParam(LIFE_STYLE_NAME_PARAM);
      }

      dispatch(
        getWeeksBookPackageWithCard(
          data,
          () => scrollToError?.(),
          stripe,
          elements as any,
        ),
      );
    }
  };

  const onReady = (event: any) => {
    const available = event?.availablePaymentMethods;
    // Stripe may call onReady before methods are known; don't treat as "no wallets".
    if (available === undefined || available === null) {
      return;
    }
    const hasWallets = hasStripeExpressWalletMethods(available);
    // Stripe often fires twice (e.g. empty flags first, then real wallets). Always upgrade false → true.
    if (lastAvailabilityReportRef.current !== hasWallets) {
      lastAvailabilityReportRef.current = hasWallets;
      if (walletProbeFallbackTimerRef.current != null) {
        window.clearTimeout(walletProbeFallbackTimerRef.current);
        walletProbeFallbackTimerRef.current = undefined;
      }
      onExpressWalletAvailabilityResolved?.(hasWallets);
    }
    if (!hasWallets) {
      return;
    }
    setVisibility('block');
    if (isEmpty(paymentSelected)) setMultiplePayments?.(available);
  };

  function getPaymentMethods(
    enabledMethods: string,
    paymentSelected: string
  ): ExpressCheckoutPaymentMethodsOption | {} {
    // If both `enabledMethods` and `paymentSelected` are empty, return an empty object
    if (isEmpty(enabledMethods) && isEmpty(paymentSelected)) {
      return {};
    }

    // If `enabledMethods` is empty but `paymentSelected` has a value, enable only the selected method
    if (isEmpty(enabledMethods)) {
      return {
        amazonPay: 'never',
        applePay: 'never',
        googlePay: 'never',
        link: 'never',
        paypal: 'never',
      };
    }

    const methods: ExpressCheckoutPaymentMethodsOption = {
      amazonPay: enabledMethods.includes('amazon_pay')
        ? paymentSelected === 'amazon_pay' || paymentSelected === ''
          ? 'auto'
          : 'never'
        : 'never',
      applePay: enabledMethods.includes('apple_pay')
        ? paymentSelected === 'apple_pay' || paymentSelected === ''
          ? 'always'
          : 'never'
        : 'never',
      googlePay: enabledMethods.includes('google_pay')
        ? paymentSelected === 'google_pay' || paymentSelected === ''
          ? 'always'
          : 'never'
        : 'never',
      link: enabledMethods.includes('link')
        ? paymentSelected === 'link' || paymentSelected === ''
          ? 'auto'
          : 'never'
        : 'never',
      paypal: enabledMethods.includes('paypal')
        ? paymentSelected === 'paypal' || paymentSelected === ''
          ? 'auto'
          : 'never'
        : 'never',
    };

    return methods;
  }

  return (
    <div id="checkout-page" style={{ position: 'relative' }}>
      <div
        id="express-checkout-element"
        style={{
          display: visibility === 'hidden' ? 'none' : 'block',
          opacity: isDisabled ? 0.5 : 1, // visual desactivation of the botton
          cursor: isDisabled ? 'not-allowed' : 'pointer',
        }}
      >
        {/* Generate a unique key based on the payment method */}
        {useMemo(() => {
          const key = JSON.stringify(
            getPaymentMethods(
              buttonsPreferences ? buttonsPreferences[0]?.value : '',
              paymentSelected
            )
          );

          return (
            <ExpressCheckoutElement
              key={key} // Cambia la clave para forzar la reinstanciación
              onConfirm={onConfirm}
              onReady={onReady}
              options={{
                paymentMethods: getPaymentMethods(
                  buttonsPreferences ? buttonsPreferences[0]?.value : '',
                  paymentSelected
                ),
                paymentMethodOrder: [
                  'applePay',
                  'googlePay',
                  'link',
                  'paypal',
                  'amazonPay',
                ], // Orden de preferencia
              }}
            />
          );
        }, [buttonsPreferences, paymentSelected])}
      </div>
      {isDisabled && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'transparent', // Sin color para mantener la visibilidad
            cursor: 'not-allowed',
          }}
        />
      )}
    </div>
  );
}

